import React from 'react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { UserX, MailWarning, RefreshCw, Trash2, Send, Loader2, ShoppingCart, Clock, TrendingUp, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDate = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return '—'; }
};

const AdminLifecycleTab = ({ adminFetch }) => {
  const [data, setData] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [running, setRunning] = React.useState('');
  const [deleting, setDeleting] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        adminFetch(`${API_URL}/api/admin/lifecycle/segments`),
        adminFetch(`${API_URL}/api/admin/lifecycle/stats`),
      ]);
      const d = await r1.json();
      if (!r1.ok) throw new Error(d.detail || 'Failed to load');
      setData(d);
      if (r2.ok) setStats(await r2.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [adminFetch]);

  React.useEffect(() => { load(); }, [load]);

  const runSweep = async (action, label) => {
    setRunning(action);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/lifecycle/run`, {
        method: 'POST', body: JSON.stringify({ action }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      const s = d.summary || {};
      toast.success(`${label}: ${s.warnings_sent || 0} warnings, ${s.moved_to_pending || 0} queued, ${s.restock_sent || 0} restock nudges sent`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setRunning(''); }
  };

  const deleteCustomer = async (row) => {
    if (!window.confirm(`Permanently delete ${row.email}? This removes the account, addresses and profile. This cannot be undone.`)) return;
    setDeleting(row.customer_id);
    try {
      const r = await adminFetch(`${API_URL}/api/admin/lifecycle/customers/${row.customer_id}`, { method: 'DELETE' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Deleted ${row.email}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setDeleting(''); }
  };

  const sendDigest = async () => {
    setRunning('digest');
    try {
      const r = await adminFetch(`${API_URL}/api/admin/lifecycle/send-digest`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(`Digest emailed to ${d.sent_to}`);
      await load();
    } catch (e) { toast.error(e.message); } finally { setRunning(''); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading lifecycle segments…
      </div>
    );
  }
  if (!data) return null;

  const counts = data.counts || {};
  const s = data.settings || {};

  const MiniStat = ({ label, value, tone }) => (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className={`text-xl font-bold ${tone || 'text-slate-800'}`}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{label}</div>
    </div>
  );

  const StatTile = ({ icon: Icon, tone, label, value, sub }) => (
    <div className={`rounded-xl border p-4 ${tone}`} data-testid={`lifecycle-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
      </div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs opacity-75 mt-0.5">{sub}</div>
    </div>
  );

  const SegTable = ({ rows, showWarnedDate, showLastOrder, actions }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-200">
            <th className="py-2 pr-4 font-medium">Customer</th>
            <th className="py-2 pr-4 font-medium">Joined</th>
            {showLastOrder && <th className="py-2 pr-4 font-medium">Last order</th>}
            {showWarnedDate && <th className="py-2 pr-4 font-medium">Warned</th>}
            {actions && <th className="py-2 pr-4 font-medium text-right">Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-slate-400">No customers in this segment.</td></tr>
          )}
          {rows.map((row) => (
            <tr key={row.customer_id} className="border-b border-slate-100" data-testid={`lifecycle-row-${row.customer_id}`}>
              <td className="py-2 pr-4">
                <div className="font-medium text-slate-800">{row.name || '—'}</div>
                <div className="text-xs text-slate-500">{row.email}</div>
                {row.opted_out_all && <span className="text-[10px] text-amber-600">opted out of all email</span>}
              </td>
              <td className="py-2 pr-4 text-slate-600">{fmtDate(row.created_at)}</td>
              {showLastOrder && <td className="py-2 pr-4 text-slate-600">{fmtDate(row.last_order)}</td>}
              {showWarnedDate && <td className="py-2 pr-4 text-slate-600">{fmtDate(row.deletion_warning_sent_at)}</td>}
              {actions && <td className="py-2 pr-4 text-right">{actions(row)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-lifecycle-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Customer Re-engagement</h2>
          <p className="text-sm text-slate-500">
            Warn dormant never-purchased accounts, queue them for deletion after {s.grace_days}d grace, and nudge lapsed members to restock.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="lifecycle-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" disabled={!!running} onClick={() => runSweep('warnings', 'Warnings')} data-testid="lifecycle-send-warnings-btn">
            {running === 'warnings' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <MailWarning className="h-4 w-4 mr-1" />}
            Send warnings now
          </Button>
          <Button size="sm" disabled={!!running} onClick={() => runSweep('restock', 'Restock')} data-testid="lifecycle-send-restock-btn">
            {running === 'restock' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send restock nudges
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Clock} tone="bg-slate-50 border-slate-200 text-slate-700" label="Due for warning" value={counts.dormant_new || 0} sub={`No order · joined >${s.warning_days}d ago`} />
        <StatTile icon={MailWarning} tone="bg-amber-50 border-amber-200 text-amber-800" label="Warned in grace" value={counts.warned || 0} sub={`${s.grace_days}d to order or be deleted`} />
        <StatTile icon={UserX} tone="bg-red-50 border-red-200 text-red-700" label="Pending Deletion" value={counts.pending_deletion || 0} sub="Awaiting your final delete" />
        <StatTile icon={ShoppingCart} tone="bg-teal-50 border-teal-200 text-teal-800" label="Restock Due" value={counts.restock_due || 0} sub={`Members idle >${s.restock_days}d`} />
      </div>

      {/* Campaign results — how the re-engagement effort is paying off */}
      <Card data-testid="lifecycle-stats-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-teal-700 text-base">
              <TrendingUp className="h-5 w-5" /> Campaign results
            </CardTitle>
            <Button size="sm" variant="outline" disabled={!!running} onClick={sendDigest} data-testid="lifecycle-send-digest-btn">
              {running === 'digest' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />}
              Email me the digest
            </Button>
          </div>
          <p className="text-sm text-slate-500">All-time totals since re-engagement was enabled.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <MiniStat label="Warnings sent" value={stats?.totals?.warnings_sent ?? 0} />
            <MiniStat label="Came back & ordered" value={stats?.totals?.recovered_after_warning ?? 0} tone="text-teal-600" />
            <MiniStat label="Restock nudges" value={stats?.totals?.restock_sent ?? 0} />
            <MiniStat label="Queued for deletion" value={stats?.totals?.moved_to_pending ?? 0} />
            <MiniStat label="Deleted" value={stats?.totals?.deleted ?? 0} tone="text-red-600" />
          </div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Warned accounts that came back ({(stats?.recovered_customers || []).length})
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-slate-500 border-b border-slate-200">
                  <th className="py-2 pr-4 font-medium">Customer</th>
                  <th className="py-2 pr-4 font-medium">Recovered on</th>
                </tr>
              </thead>
              <tbody>
                {(stats?.recovered_customers || []).length === 0 && (
                  <tr><td colSpan={2} className="py-4 text-center text-slate-400">No recovered accounts yet.</td></tr>
                )}
                {(stats?.recovered_customers || []).map((row) => (
                  <tr key={row.customer_id} className="border-b border-slate-100" data-testid={`lifecycle-recovered-${row.customer_id}`}>
                    <td className="py-2 pr-4">
                      <div className="font-medium text-slate-800">{row.name || '—'}</div>
                      <div className="text-xs text-slate-500">{row.email}</div>
                    </td>
                    <td className="py-2 pr-4 text-teal-700">{fmtDate(row.reengagement_recovered_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-red-700 text-base">
            <UserX className="h-5 w-5" /> Pending Deletion — final review
          </CardTitle>
          <p className="text-sm text-slate-500">These never-purchased accounts passed the grace window. Deleting is permanent.</p>
        </CardHeader>
        <CardContent>
          <SegTable
            rows={data.pending_deletion || []}
            showWarnedDate
            actions={(row) => (
              <Button
                size="sm" variant="destructive" disabled={deleting === row.customer_id}
                onClick={() => deleteCustomer(row)} data-testid={`lifecycle-delete-${row.customer_id}`}
              >
                {deleting === row.customer_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span className="ml-1">Delete</span>
              </Button>
            )}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-amber-700 text-base">
            <MailWarning className="h-5 w-5" /> Warned — in grace window
          </CardTitle>
        </CardHeader>
        <CardContent><SegTable rows={data.warned || []} showWarnedDate /></CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-slate-700 text-base">
            <Clock className="h-5 w-5" /> Due for warning (not yet sent)
          </CardTitle>
        </CardHeader>
        <CardContent><SegTable rows={data.dormant_new || []} /></CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-teal-700 text-base">
            <ShoppingCart className="h-5 w-5" /> Restock due — lapsed members
          </CardTitle>
        </CardHeader>
        <CardContent><SegTable rows={data.restock_due || []} showLastOrder /></CardContent>
      </Card>
    </div>
  );
};

export default AdminLifecycleTab;
